import React from 'react';
import { Users, Scale, Monitor, Zap, ArrowRight, Building2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { hrBenefits } from '../data/mock';

const iconMap = {
  Users: Users,
  Scale: Scale,
  Monitor: Monitor,
  Zap: Zap
};

const HRSection = () => {
  return (
    <section id="hr-section" className="py-24 bg-gradient-to-br from-gray-50 via-white to-orange-50/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-orange-500 rounded-2xl mb-6">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            HR Mobility Solutions for
            <span className="text-orange-500"> Companies</span>
          </h2>
          <p className="text-xl text-gray-600">
            Streamline your expat relocation process with our comprehensive corporate partnership program.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left: Image/Visual */}
          <div className="relative">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&q=80"
                alt="Business team collaboration"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-500/30 to-transparent"></div>
            </div>
            
            {/* Floating Stats Card */}
            <div className="absolute -bottom-8 -right-8 bg-white rounded-xl shadow-2xl p-6 border-2 border-orange-200">
              <div className="text-center">
                <div className="text-4xl font-bold text-orange-500 mb-1">50+</div>
                <div className="text-sm text-gray-600">Corporate Partners</div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className="space-y-6">
            <h3 className="text-3xl font-bold text-gray-900">
              Your Trusted Partner for Employee Relocation
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              When relocating international talent to Germany, your employees face complex financial and administrative challenges. We provide end-to-end support that ensures a smooth transition, allowing your team to focus on their work from day one.
            </p>
            
            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <p className="text-gray-800 font-medium">
                "We handle everything from bank accounts and insurance to long-term wealth building, giving your HR team peace of mind and your employees the support they deserve."
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={() => {
                  const element = document.getElementById('contact');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Partner With Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-all duration-300"
                onClick={() => window.open('mailto:kontakt@gr-prosperity.de')}
              >
                Request Information
              </Button>
            </div>
          </div>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {hrBenefits.map((benefit) => {
            const IconComponent = iconMap[benefit.icon];
            return (
              <Card
                key={benefit.id}
                className="text-center hover:shadow-lg transition-all duration-300 border-2 hover:border-orange-200 bg-white group"
              >
                <CardHeader>
                  <div className="w-14 h-14 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <CardTitle className="text-xl text-gray-900 group-hover:text-orange-500 transition-colors">
                    {benefit.title}
                  </CardTitle>
                  <CardDescription className="text-gray-600 mt-2">
                    {benefit.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            );
          })}
        </div>

        {/* HR Tool Highlight */}
        <div className="mt-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 md:p-12 text-white shadow-2xl">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-sm font-medium mb-4">
                <Monitor className="w-4 h-4 mr-2" />
                Digital HR Tool
              </div>
              <h3 className="text-3xl font-bold mb-4">
                Manage All Your Expats in One Place
              </h3>
              <p className="text-orange-50 text-lg mb-6">
                Our dedicated HR platform allows you to organize, track, and manage the complete relocation process for all your international employees. Get real-time updates and maintain full visibility.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Track relocation progress for each employee',
                  'Centralized document management',
                  'Automated status updates and notifications',
                  'Dedicated support team for your organization'
                ].map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <ArrowRight className="w-4 h-4" />
                    </div>
                    <span className="text-orange-50">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="text-center">
                <Monitor className="w-20 h-20 text-white mx-auto mb-4 opacity-80" />
                <p className="text-white font-semibold text-lg mb-2">
                  Request Platform Demo
                </p>
                <p className="text-orange-50 text-sm mb-6">
                  See how our HR tool simplifies expat management
                </p>
                <Button
                  onClick={() => {
                    const element = document.getElementById('contact');
                    if (element) element.scrollIntoView({ behavior: 'smooth' });
                  }}
                  variant="secondary"
                  size="lg"
                  className="bg-white text-orange-600 hover:bg-orange-50 w-full transition-colors"
                >
                  Schedule Demo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HRSection;
